import { mutate } from 'swr';

import {
  invalidateWorkspaceStatsIfNeeded,
  matchKeyPartial,
} from '../hooks/useSwr';
import {
  CreateDatasetRequest,
  DatasetSchema,
  DatasetTransformationSchema,
  PatchSessionBody,
  PostSessionBody,
  RepoExampleCreateSchema,
  SessionSchema,
} from '../types/schema';
import {
  apiDatasetsPath,
  apiExamplesPath,
  apiSessionsPath,
} from '../utils/constants';
import { fetcher } from './fetcher';

export const createSession = async (
  tenantId: string,
  json: PostSessionBody
) => {
  const result = await fetcher<SessionSchema, never, PostSessionBody>({
    url: apiSessionsPath,
    method: 'POST',
    json,
    headers: { 'X-Tenant-Id': tenantId },
  });

  // Invalidate workspace stats after successful session creation
  invalidateWorkspaceStatsIfNeeded(apiSessionsPath, 'POST');

  return result;
};

export const updateSession = async (
  tenantId: string,
  id: string,
  json: PatchSessionBody
) => {
  const result = await fetcher<SessionSchema, never, PatchSessionBody>({
    url: `${apiSessionsPath}/${id}`,
    method: 'PATCH',
    json,
    headers: { 'X-Tenant-Id': tenantId },
  });

  // Invalidate workspace stats after successful session update
  invalidateWorkspaceStatsIfNeeded(`${apiSessionsPath}/${id}`, 'PATCH');

  return result;
};

export const createDataset = async (
  tenantId: string | undefined,
  {
    name,
    description,
    data_type,
    inputs_schema_definition,
    outputs_schema_definition,
    transformations,
    extra,
  }: CreateDatasetRequest
) => {
  const result = await fetcher<DatasetSchema, never, CreateDatasetRequest>({
    url: apiDatasetsPath,
    method: 'POST',
    json: {
      name,
      description,
      data_type,
      inputs_schema_definition,
      outputs_schema_definition,
      transformations,
      extra,
    },
    headers: tenantId ? { 'X-Tenant-Id': tenantId } : undefined,
  });

  // Invalidate workspace stats after successful dataset creation
  invalidateWorkspaceStatsIfNeeded(apiDatasetsPath, 'POST');

  return result;
};

export interface UpdateDatasetRequest extends UpdateDatasetRequestBase {
  id: string;
}

interface UpdateDatasetRequestBase {
  name: string;
  description: string;
  data_type?: DatasetSchema['data_type'];
  inputs_schema_definition?: Record<string, unknown> | null;
  outputs_schema_definition?: Record<string, unknown> | null;
  transformations?: DatasetTransformationSchema[];
  patch_examples?: Record<string, RepoExampleCreateSchema>;
}

export const updateDataset = async (
  tenantId: string,
  {
    id,
    name,
    description,
    data_type,
    inputs_schema_definition,
    outputs_schema_definition,
    transformations,
    patch_examples,
  }: UpdateDatasetRequest
) => {
  const result = await fetcher<DatasetSchema, never, UpdateDatasetRequestBase>({
    url: `${apiDatasetsPath}/${id}`,
    method: 'PATCH',
    json: {
      name,
      description,
      data_type,
      ...(inputs_schema_definition === undefined
        ? {}
        : { inputs_schema_definition }),
      ...(outputs_schema_definition === undefined
        ? {}
        : { outputs_schema_definition }),
      ...(transformations === undefined ? {} : { transformations }),
      patch_examples,
    },
    headers: { 'X-Tenant-Id': tenantId },
  });

  // Invalidate workspace stats after successful dataset update
  invalidateWorkspaceStatsIfNeeded(`${apiDatasetsPath}/${id}`, 'PATCH');
  mutate(matchKeyPartial({ url: `${apiDatasetsPath}/${id}` }));
  if (patch_examples != null && Object.keys(patch_examples).length > 0) {
    mutate(matchKeyPartial({ url: apiExamplesPath }));
  }

  return result;
};
